import { useLocation, useParams } from "react-router";
import { useEffect, useState } from "react";
import { Badge, Modal, Tabs, type TabOption } from "@insights/ui";
import { Loading } from "../../../shared/components";
import { useWorkspaceContext } from "../../../shared/hooks/useWorkspaceContext";
import { parseBuildTargets } from "../helpers/build-graph-layout";
import type { BuildDetail } from "../types/build";
import { ProvenancePanel } from "./ProvenancePanel";
import { BuildGraphPreview, type BuildGraphNodeCount } from "./BuildGraphPreview";
import { BuildInfo } from "./BuildInfo";

type BuildTab = "graph" | "info" | "provenance";
type BuildGraphModalProps = {
  detail: BuildDetail | undefined;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

export function BuildGraphModal({
  detail,
  loading = false,
  onOpenChange,
  open,
}: BuildGraphModalProps) {
  const { workspaceId } = useParams();
  const location = useLocation();
  const provenanceMode = new URLSearchParams(location.search).get("provenance");
  const workspace = useWorkspaceContext(workspaceId);
  const provenanceEnabled = workspace.data?.features.provenance === true;
  const [nodeCount, setNodeCount] = useState<BuildGraphNodeCount | null>(null);
  const [tab, setTab] = useState<BuildTab>(provenanceMode === null ? "graph" : "provenance");
  const selectedTab = tab === "provenance" && !provenanceEnabled ? "graph" : tab;

  useEffect(() => {
    if (open) {
      setTab(provenanceMode === null ? "graph" : "provenance");
      setNodeCount(null);
    }
  }, [open, detail?.build.id, provenanceMode]);

  const tabs: TabOption<BuildTab>[] = [
    {
      value: "graph",
      label: "Graph",
      content: detail && (
        <BuildGraphPreview
          graph={detail.graph}
          initialTargets={parseBuildTargets(detail.build.context_json)}
          key={detail.build.id}
          onNodeCountChange={setNodeCount}
        />
      ),
    },
    {
      value: "info",
      label: "Build Info",
      content: detail && <BuildInfo detail={detail} />,
    },
  ];
  if (provenanceEnabled) {
    tabs.push({
      value: "provenance",
      label: "Provenance",
      content: detail && (
        <div className="build-provenance-scroll" role="region" aria-label="Provenance artifacts" tabIndex={0}>
          <ProvenancePanel
            workspaceId={workspaceId}
            buildId={detail.build.id}
            state={provenanceMode === "state"}
            collapsible={false}
          />
        </div>
      ),
    });
  }

  return (
    <Modal
      className="build-graph-modal"
      description={
        selectedTab === "provenance"
          ? "Review artifact evidence, transformation declarations and the provenance chain."
          : selectedTab === "info"
            ? "Review execution metadata, artifacts, and the submitted graph."
            : "Explore build tasks and their dependencies. Arrows point toward each task’s dependencies."
      }
      onOpenChange={onOpenChange}
      open={open}
      title={
        <span className="build-graph-modal-title">
          <span>Build details</span>
          {selectedTab === "graph" && nodeCount && (
            <Badge>{nodeCount.visible} of {nodeCount.total} nodes</Badge>
          )}
        </span>
      }
    >
      <div className="build-detail-modal-content">
        {loading ? <Loading /> : (
          <Tabs
            className="build-detail-tabs"
            label="Build details"
            options={tabs}
            value={selectedTab}
            onValueChange={setTab}
          />
        )}
      </div>
    </Modal>
  );
}
